import * as m from '@staiter/i18n';
import type { RuntimeErrorTelemetry } from '@staiter/shared';
import { AlertTriangle, Home, RotateCw } from 'lucide-react';
import { ApiRequestError } from '~/lib/api/client';
import { renderApiError } from '~/lib/api/error-renderer';
import { captureRuntimeError } from '~/lib/sentry/capture';
import styles from './AppErrorFallback.module.css';

/**
 * E-FE.3 — Root error boundary. Полностью локализован через `m.*()`.
 *
 * НЕ показывает `err.message` / stack-trace пользователю (могут быть English
 * или раскрыть внутренние детали). Полный error/stack уходит в Sentry и telemetry.
 */

export type AppErrorFallbackProps = {
  error: Error;
  reset?: () => void;
};

export function AppErrorFallback({ error, reset }: AppErrorFallbackProps): JSX.Element {
  const rendered = renderApiError(error);
  const traceId =
    rendered.trace_id ?? (error instanceof ApiRequestError ? error.traceId : undefined);

  if (typeof window !== 'undefined') {
    captureRuntimeError(error, {
      route: window.location.pathname,
      url: window.location.href,
      trace_id: traceId,
    });
    try {
      // Schema-conformant payload (packages/shared/telemetry-contracts.ts).
      // Шлём И code (структурный, для filtering/dashboards), И message (raw
      // error.message — оригинальный JS-текст, бесценен в логах).
      const payload: RuntimeErrorTelemetry = {
        route: window.location.pathname,
        ...(rendered.code ? { code: rendered.code } : {}),
        ...(error.message ? { message: error.message.slice(0, 2000) } : {}),
        ...(error.stack ? { stack: error.stack.slice(0, 5000) } : {}),
        ...(traceId ? { trace_id: traceId } : {}),
        user_agent: navigator.userAgent,
        url: window.location.href,
      };
      void fetch('/api/v1/telemetry/runtime-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {
        /* double-fail guard */
      });
    } catch {
      /* ignore */
    }
  }

  const description = m.error_app_crashed_body({
    trace: traceId ? traceId.slice(0, 8) : '—',
  });

  return (
    <div className={styles.root} role="alert">
      <div className={styles.panel}>
        <AlertTriangle size={48} className={styles.icon} aria-hidden="true" />
        <h2 className={styles.title}>{m.error_app_crashed_title()}</h2>
        <p className={styles.description}>{description}</p>
        {traceId ? <code className={styles.trace}>trace: {traceId}</code> : null}
        <div className={styles.actions}>
          {reset ? (
            <button type="button" className={styles.btnPrimary} onClick={reset}>
              <RotateCw size={16} aria-hidden="true" />
              {m.error_app_crashed_reload_btn()}
            </button>
          ) : null}
          <a href="/" className={styles.btnSecondary}>
            <Home size={16} aria-hidden="true" />
            {m.error_fallback_go_home()}
          </a>
        </div>
      </div>
    </div>
  );
}
